import publicUrls from "../../routes/publicUrls";
import React, { useState, useRef, useCallback, useEffect, useMemo } from "react";
import { useParams } from "react-router-dom";
import { Box, CircularProgress, useMediaQuery, InputAdornment, IconButton, Container, Divider, SwipeableDrawer, Button, Typography, Stack, OutlinedInput } from "@mui/material";

import SearchIcon from "@mui/icons-material/Search";
import FavoriteIcon from "@mui/icons-material/Favorite";
import FavoriteBorderIcon from "@mui/icons-material/FavoriteBorder";
import GridViewIcon from "@mui/icons-material/GridView";
import ViewListIcon from "@mui/icons-material/ViewList";
import BurstModeIcon from "@mui/icons-material/BurstMode";
import FilterListIcon from "@mui/icons-material/FilterList";

import LotData from "./components/LotData";
import SidebarFilters from "./components/SidebarFilters";
import { filterArtist, addfavorite, removefavorite, getFilter } from "../../Model/Auction";
import { getToken } from "../../Model/User";
import { getUser } from "../../Model/LocalStorage";
import { encodeId, decodeId } from "../../hash";
import { GetURL } from "../../config";
import LotActivityCard from "./components/LotActivityCard";

const getAuctionCacheKey = (id, page = 1, search = "") => {
  return `lots_page_${page}_id_${id}_search_${search}`;
};

export default function Lot({ data }) {
  const isDesktop = useMediaQuery("(min-width:1100px)");
  const { id, slug } = useParams();
  const token = getToken();

  const auctionInfo = data?.[0] || {};
  const auctionType = auctionInfo.auctionType || "";
  const showHp = auctionInfo.showHp || "";

  const [pageNumber, setPageNumber] = useState(1);
  const [viewMode, setViewMode] = useState("grid");
  const [search, setSearch] = useState("");
  const [filter, setFilter] = useState("");
  const [searchCat, setSearcHCategory] = useState("");
  const [favorite, setFavorite] = useState("");
  const [favoriteStat, setFavoriteStat] = useState(true);
  const [yourId, setYourId] = useState(null);
  const [limit, setLimit] = useState(10); // 1. Dapat may state na ganito
  const [query, setQuery] = useState("");
  const [filteredResults, setFilteredResults] = useState([]);
  const [categoryData, setCategory] = useState([]);
  const [loadingCat, setLoadingCat] = useState(false);
  const [openDrawer, setOpenDrawer] = useState(false);

  // Countdown States
  const [timeLeft, setTimeLeft] = useState({
    days: "00",
    hours: "00",
    minutes: "00",
    seconds: "00",
  });
  const [status, setStatus] = useState("waiting");
  const [dateStart, setDateStart] = useState("");
  const [dateEnd, setDateEnd] = useState("");
  const [timeStart, setTimeStart] = useState("");
  const [timeEnd, setTimeEnd] = useState("");

  const { lotdata, setLot, loading, hasMore, totalLOts } = LotData(
    pageNumber,
    id,
    search,
    "",
    searchCat,
    favorite,
    0
  );

  const hasActiveFilter = useMemo(
    () => search !== "" || searchCat !== "" || !favoriteStat,
    [search, searchCat, favoriteStat]
  );

  const TimerBox = ({ value, label }) => (
    <Box sx={{ textAlign: "center", mx: 0.5 }}>
      <Box
        sx={{
          bgcolor: "#fff",
          color: "#000",
          width: { xs: 45, md: 55 },
          height: { xs: 45, md: 55 },
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          borderRadius: "4px",
          mb: 0.5,
        }}
      >
        <Typography
          sx={{ fontSize: { xs: "1.1rem", md: "1.5rem" }, fontWeight: 700 }}
        >
          {value}
        </Typography>
      </Box>
      <Typography sx={{ fontSize: "0.6rem", fontWeight: 800, color: "#999" }}>
        {label}
      </Typography>
    </Box>
  );

  useEffect(() => {
    if (lotdata && lotdata.length > 0) {
      const key = getAuctionCacheKey(id, pageNumber, search);

      try {
        // 1. MINIMIZE DATA: Kunin lang ang kailangan para sa UI
        // Binabawasan nito ang size ng cache ng hanggang 90%
        const minimizedItems = lotdata.map((item) => ({
          data: {
            id: item.data?.id,
            lot_number: item.data?.lot_number,
            title: item.data?.title,
            artist: item.data?.artist,
            auction_name: item.data?.auction_name,
            auction_id: item.data?.auction_id,
            hammer_price: item.data?.hammer_price,
            starting_price: item.data?.starting_price,
            lot_status: item.data?.lot_status,
            isWithdrawn: item.data?.isWithdrawn,
            fav: item.data?.fav,
          },
          image:
            item.image?.length > 0 ? [{ img_url: item.image[0].img_url }] : [],
        }));

        const storageData = {
          items: minimizedItems,
          totalCount: totalLOts,
          timestamp: Date.now(),
        };

        // 2. SAVE TO SESSION STORAGE
        sessionStorage.setItem(key, JSON.stringify(storageData));
      } catch (error) {
        // 3. ERROR HANDLING: Kapag QuotaExceededError, linisin ang lumang cache
        if (
          error.name === "QuotaExceededError" ||
          error.name === "NS_ERROR_DOM_QUOTA_REACHED"
        ) {
          console.log(
            "Session storage full. Clearing old auction cache keys..."
          );

          // Burahin lahat ng keys na nagsisimula sa 'lots_page_' o cache pattern mo
          Object.keys(sessionStorage).forEach((k) => {
            if (k.includes("lots_page_") || k.includes("auction_cache_")) {
              sessionStorage.removeItem(k);
            }
          });

          // Subukan ulit i-save pagkatapos maglinis
          try {
            const fallbackData = {
              items: lotdata, // or minimizedItems
              totalCount: totalLOts,
              timestamp: Date.now(),
            };
            sessionStorage.setItem(key, JSON.stringify(fallbackData));
          } catch (retryError) {
            console.error("Critical: Storage still full after clearing.");
          }
        }
      }
    }
  }, [lotdata, totalLOts, id, pageNumber, search]);

  useEffect(() => {
    if (!id) return;
    async function fetchDates() {
      try {
        const values = await getFilter(decodeId(id));
        if (values && values[0]) {
          setDateStart(values[0].date_start || "");
          setDateEnd(values[0].date_end || "");
          setTimeStart(values[0].time_start || "00:00:00");
          setTimeEnd(values[0].time_end || "00:00:00");
        }
      } catch (error) {
        console.error("Fetch dates error:", error);
      }
    }
    fetchDates();
  }, [id]);

  useEffect(() => {
    let interval;
    const startCountdown = async () => {
      try {
        const response = await fetch(`${GetURL}/api/server-time`);
        const sTimeData = await response.json();
        let serverTime = new Date(sTimeData.serverTime);
        const parseDateTime = (d, t) => {
          if (!d) return null;
          const datePart = d.split("T")[0];
          return new Date(`${datePart}T${t}`);
        };
        const startDate = parseDateTime(dateStart, timeStart);
        const endDate = parseDateTime(dateEnd, timeEnd);
        interval = setInterval(() => {
          const now = serverTime.getTime();
          let targetDiff = 0;
          if (startDate && now < startDate.getTime()) {
            setStatus("waiting");
            targetDiff = startDate.getTime() - now;
          } else if (endDate && now < endDate.getTime()) {
            setStatus("live");
            targetDiff = endDate.getTime() - now;
          } else {
            clearInterval(interval);
            setStatus("ended_auction");
            setTimeLeft({
              days: "00",
              hours: "00",
              minutes: "00",
              seconds: "00",
            });
            return;
          }
          const d = Math.floor(targetDiff / (1000 * 60 * 60 * 24));
          const h = Math.floor((targetDiff / (1000 * 60 * 60)) % 24);
          const m = Math.floor((targetDiff / (1000 * 60)) % 60);
          const s = Math.floor((targetDiff / 1000) % 60);
          setTimeLeft({
            days: String(d).padStart(2, "0"),
            hours: String(h).padStart(2, "0"),
            minutes: String(m).padStart(2, "0"),
            seconds: String(s).padStart(2, "0"),
          });
          serverTime = new Date(serverTime.getTime() + 1000);
        }, 1000);
      } catch (e) {
        console.error("Timer error:", e);
      }
    };
    startCountdown();
    return () => clearInterval(interval);
  }, [dateStart, dateEnd, timeStart, timeEnd, auctionType]);

  useEffect(() => {
    const init = async () => {
      setLoadingCat(true);
      const res = await filterArtist(decodeId(id));
      setFilteredResults(
        res.data ? res.data.filter((o) => o.artist !== null) : []
      );
      setCategory(res.categories || []);
      const user = await getUser();
      setYourId(user?.id);
      setLoadingCat(false);
    };
    init();
  }, [id]);

  const observer = useRef();

  useEffect(() => {
    const saved = sessionStorage.getItem(`lotdata-${slug}`);
    if (saved) {
      const {
        scrollY,
        pageNumber: savedPage,
        lotdata: savedData,
      } = JSON.parse(saved);
      setPageNumber(savedPage);
      setLot(savedData);
      window.scrollTo(0, scrollY);
      sessionStorage.removeItem(`lotdata-${slug}`);
    }
  }, [slug]);

  const lastBookElementRef = useCallback(
    (node) => {
      if (loading) return;
      if (observer.current) observer.current.disconnect();
      observer.current = new IntersectionObserver((entries) => {
        if (entries[0].isIntersecting && hasMore) {
          // Only increment pageNumber kung hindi nag-restore
          setPageNumber((prev) => prev + 1);
        }
      });
      if (node) observer.current.observe(node);
    },
    [loading, hasMore]
  );

  //   (node) => {
  //     observer.current = new IntersectionObserver((entries) => {
  //         setPageNumber((prev) => prev + 1);
  //     });
  //   },
  //   [loading, hasMore]
  // );

  const handleSearch = (e) => {
    e.preventDefault();
    setSearch(filter);
    setPageNumber(1);
  };
  const handleArtistPick = (name) => {
    setSearch(name);
    setSearcHCategory("");
    setPageNumber(1);
    setOpenDrawer(false);
  };
  const handleCatPick = (cat) => {
    setSearcHCategory(cat);
    setSearch("");
    setPageNumber(1);
    setOpenDrawer(false);
  };
  const handleReset = () => {
    setSearch("");
    setFilter("");
    setSearcHCategory("");
    setFavorite("");
    setFavoriteStat(true);
    setPageNumber(1);
    setQuery("");
    setOpenDrawer(false);
  };

  const handleFavoriteToggle = async (lotId, isAdd) => {
    if (!token) {
      const currentUrl = window.location.pathname + window.location.search;
      window.location.href = `/login?redirect=${encodeURIComponent(
        currentUrl
      )}`;
      return;
    }
    const updated = lotdata.map((l) =>
      l.data.id === lotId
        ? { ...l, data: { ...l.data, fav: isAdd ? 1 : 0 } }
        : l
    );
    setLot(updated);
    isAdd
      ? await addfavorite({ lot_id: lotId })
      : await removefavorite({ lot_id: lotId });
  };

  const toggleFavoriteFilter = () => {
    if (!token) {
      const currentUrl = window.location.pathname + window.location.search;
      window.location.href = `/login?redirect=${encodeURIComponent(
        currentUrl
      )}`;
      return;
    }
    setFavoriteStat(!favoriteStat);
    setFavorite(favoriteStat ? yourId : "");
    setPageNumber(1);
  };

  return (
    <Box sx={{ bgcolor: "#fff", minHeight: "100vh" }}>
      

      
<SwipeableDrawer anchor="left" open={openDrawer} onClose={()=>setOpenDrawer(false)} onOpen={()=>setOpenDrawer(true)} PaperProps={{sx:{width:320}}}>
  <Box sx={{p:2}}>
    <SidebarFilters
      setLimit={setLimit}
      filteredResults={filteredResults}
      categoryData={categoryData}
      onArtistSelect={handleArtistPick}
      onCategorySelect={handleCatPick}
      onReset={handleReset}
      hasActiveFilter={hasActiveFilter}
      query={query}
      setQuery={setQuery}
      limit={limit} // <--- GINAWANG VARIABLE PARA SUMUNOD SA STATE
      loadingCat={loadingCat}
    />
  </Box>
</SwipeableDrawer>

      <Container maxWidth="xl" sx={{ pt: 4 }}>
        {auctionType === "TIME_AUCTION" && status !== "ended_auction" && (
          <Box sx={{ mb: 5, textAlign: "center" }}>
            <Typography
              sx={{
                fontSize: "0.7rem",
                fontWeight: 800,
                color: "#999",
                letterSpacing: 3,
                mb: 2,
              }}
            >
              {status === "live" ? "BIDDING ENDS IN" : "LIVE BIDDING STARTS IN"}
            </Typography>
            <Stack direction="row" spacing={1} justifyContent="center">
              <TimerBox value={timeLeft.days} label="DAYS" />
              <TimerBox value={timeLeft.hours} label="HOURS" />
              <TimerBox value={timeLeft.minutes} label="MINS" />
              <TimerBox value={timeLeft.seconds} label="SECS" />
            </Stack>
          </Box>
        )}

        <Divider sx={{ borderColor: "#eee" }} />

        <Box sx={{ display: "flex" }}>
          {isDesktop && (
            <Box
              sx={{
                width: "280px",
                flexShrink: 0,
                borderRight: "1px solid #eee",
                pt: 2,
              }}
            >
              <SidebarFilters
                setLimit={setLimit} // 2. DAPAT EKSAKTO ITONG PANGALAN NA ITO
                filteredResults={filteredResults}
                categoryData={categoryData}
                onArtistSelect={handleArtistPick}
                onCategorySelect={handleCatPick}
                onReset={handleReset}
                hasActiveFilter={hasActiveFilter}
                query={query}
                setQuery={setQuery}
                limit={limit}
                loadingCat={loadingCat}
              />
            </Box>
          )}

          <Box sx={{ flexGrow: 1, pl: isDesktop ? 4 : 0, pt: 2 }}>
            <Stack
              direction="column"
              spacing={2}
              sx={{ mb: 4, pb: 2, borderBottom: "1px solid #eee" }}
            >
              <Box
                sx={{
                  display: "flex",
                  flexDirection: isDesktop ? "row" : "column",
                  justifyContent: "space-between",
                  alignItems: "center",
                  gap: 2,
                }}
              >
                <Stack
                  direction="row"
                  spacing={1}
                  alignItems="center"
                  sx={{
                    width: isDesktop ? "auto" : "100%",
                    justifyContent: "space-between",
                  }}
                >
                  <Stack direction="row" spacing={1} alignItems="center">
                    

                    <BurstModeIcon sx={{ fontSize: "1rem", color: "#999" }} />
                    <Typography
                      sx={{
                        fontWeight: 800,
                        fontSize: "0.7rem",
                        color: "#999",
                      }}
                    >
                      {totalLOts} LOTS
                    </Typography>
                  </Stack>

                  <Stack direction="row" spacing={1} alignItems="center">
                    <Button
                      variant="text"
                      startIcon={
                        favoriteStat ? (
                          <FavoriteBorderIcon sx={{ fontSize: "1rem" }} />
                        ) : (
                          <FavoriteIcon
                            sx={{ color: "red", fontSize: "1rem" }}
                          />
                        )
                      }
                      onClick={toggleFavoriteFilter}
                      sx={{
                        color: "#000",
                        fontWeight: 800,
                        fontSize: "0.7rem",
                        letterSpacing: 1,
                      }}
                    >
                      {favoriteStat ? "SHOW FAVORITES" : "SHOWING FAVORITES"}
                    </Button>

                    {!isDesktop && (
                      <Box
                        sx={{
                          display: "flex",
                          border: "1px solid #eee",
                          height: 32,
                          ml: 1,
                        }}
                      >
                        <IconButton
                          onClick={() => setViewMode("grid")}
                          sx={{
                            borderRadius: 0,
                            bgcolor: viewMode === "grid" ? "#000" : "#fff",
                            color: viewMode === "grid" ? "#fff" : "#000",
                            p: 0.5,
                          }}
                        >
                          <GridViewIcon sx={{ fontSize: "1rem" }} />
                        </IconButton>
                        <IconButton
                          onClick={() => setViewMode("list")}
                          sx={{
                            borderRadius: 0,
                            bgcolor: viewMode === "list" ? "#000" : "#fff",
                            color: viewMode === "list" ? "#fff" : "#000",
                            p: 0.5,
                          }}
                        >
                          <ViewListIcon sx={{ fontSize: "1rem" }} />
                        </IconButton>
                      </Box>
                    )}
                  </Stack>
                </Stack>

                <Box
                  sx={{
                    display: "flex",
                    gap: 1.5,
                    width: isDesktop ? "auto" : "100%",
                    alignItems: "center",
                  }}
                >
                  {!isDesktop && (
                    <Button
                      onClick={() => setOpenDrawer(true)}
                      startIcon={<FilterListIcon sx={{ fontSize: "1.1rem" }} />}
                      sx={{
                        color: "#000",
                        fontWeight: 700,
                        fontSize: 12,
                        letterSpacing: 1.2,
                        border: "0.5px solid #000", // Mas solid na look para sa main action
                        borderRadius: 0,
                        px: 2,
                        height: 40, // Pantay sa search bar
                        minWidth: "fit-content",
                        transition: "all 0.3s ease",
                        "&:active": { bgcolor: "#f5f5f5" },
                      }}
                    >
                      FILTER
                    </Button>
                  )}

                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: isDesktop ? "flex-end" : "center",
                      width: "100%",
                    }}
                  >
                    <form
                      onSubmit={handleSearch}
                      style={{ width: isDesktop ? "500px" : "100%" }}
                    >
                      <OutlinedInput
                        fullWidth
                        placeholder="Search artist or artwork..."
                        value={filter}
                        onChange={(e) => setFilter(e.target.value)}
                        sx={{
                          height: 45, // Ginawang 45 para mas bold tingnan
                          borderRadius: 0,
                          fontSize: "0.9rem",
                          bgcolor: "#fbfbfb",
                          "& .MuiOutlinedInput-notchedOutline": {
                            borderColor: "#e0e0e0",
                          },
                          "&:hover .MuiOutlinedInput-notchedOutline": {
                            borderColor: "#000",
                          },
                          "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                            borderColor: "#000",
                            borderWidth: "1px",
                          },
                        }}
                        endAdornment={
                          <InputAdornment position="end">
                            <IconButton
                              size="small"
                              type="submit"
                              sx={{ color: "#000" }}
                            >
                              <SearchIcon sx={{ fontSize: "1.3rem" }} />
                            </IconButton>
                          </InputAdornment>
                        }
                      />
                    </form>
                  </Box>

                  {isDesktop && (
                    <Box
                      sx={{
                        display: "flex",
                        border: "1px solid #e0e0e0",
                        height: 45,
                        ml: 1,
                      }}
                    >
                      <IconButton
                        onClick={() => setViewMode("grid")}
                        sx={{
                          borderRadius: 0,
                          width: 45,
                          bgcolor: viewMode === "grid" ? "#000" : "transparent",
                          color: viewMode === "grid" ? "#fff" : "#999",
                          "&:hover": {
                            bgcolor: viewMode === "grid" ? "#000" : "#f5f5f5",
                          },
                        }}
                      >
                        <GridViewIcon sx={{ fontSize: "1.1rem" }} />
                      </IconButton>
                      <Divider
                        orientation="vertical"
                        flexItem
                        sx={{ bgcolor: "#e0e0e0" }}
                      />
                      <IconButton
                        onClick={() => setViewMode("list")}
                        sx={{
                          borderRadius: 0,
                          width: 45,
                          bgcolor: viewMode === "list" ? "#000" : "transparent",
                          color: viewMode === "list" ? "#fff" : "#999",
                          "&:hover": {
                            bgcolor: viewMode === "list" ? "#000" : "#f5f5f5",
                          },
                        }}
                      >
                        <ViewListIcon sx={{ fontSize: "1.1rem" }} />
                      </IconButton>
                    </Box>
                  )}
                </Box>
              </Box>
            </Stack>

            <Box
              className="lot-catalog-grid"
              sx={{
                display: "grid",
                gridTemplateColumns:
                viewMode === "grid"
                  ? {
                      xs: "1fr",
                      sm: "1fr 1fr",
                      md: "1fr 1fr 1fr",
                      xl: "repeat(4, minmax(0, 1fr))",
                    }
                  : "1fr",
                columnGap: viewMode === "grid" ? { xs: 2.5, md: 3.5 } : 0,
                rowGap: viewMode === "grid" ? { xs: 5, md: 6 } : 2, // Inadjust ang gap para sa hover space
              }}
            >
              {lotdata.map((value, i) => {
                const handleNavigate = () => {
                  sessionStorage.setItem(
                    `lotdata-${slug}`,
                    JSON.stringify({
                      scrollY: window.scrollY,
                      pageNumber,
                      lotdata,
                    })
                  );
                  const cleanTitle =
                    value.data.title === null
                      ? "untitle"
                      : value.data.title.replace(/[^a-zA-Z0-9-_]/g, "");
                  window.location.href = publicUrls.lot(
                    slug,
                    encodeId(value.data.auction_id),
                    value.data.lot_number,
                    cleanTitle
                  );
                };

                return (
                  <Box
                    key={`${value.data.id || value.data.lot_number}-${i}`}
                    ref={lotdata.length === i + 1 ? lastBookElementRef : null}
                    sx={{ minWidth: 0, height: "100%" }}
                  >
                    <LotActivityCard
                      value={value}
                      onNavigate={handleNavigate}
                      onFavorite={() =>
                        handleFavoriteToggle(
                          value.data.id,
                          value.data.fav !== 1
                        )
                      }
                      showHp={showHp}
                      status={status}
                      yourId={yourId}
                      viewMode={viewMode}
                    />
                  </Box>
                );
              })}
            </Box>

            {loading && (
              <Box sx={{ textAlign: "center", py: 5 }}>
                <CircularProgress size={30} color="inherit" />
              </Box>
            )}

            
            {!hasMore && lotdata.length > 0 && !loading && (
              <Box sx={{ textAlign: "center", py: 8 }}>
                <Typography
                  sx={{ color: "#ccc", fontSize: "0.8rem", letterSpacing: 1 }}
                >
                  END OF RESULTS
                </Typography>
              </Box>
            )}
          </Box>
        </Box>
      </Container>
    </Box>
  );
}
